// src/config/notificationConfig.js
/**
 * Notification System Configuration
 * Uses standard PWA colors from tailwind.config.js (farmgreen, farmgold)
 * Only uses color where it adds meaning (success/error/warning)
 */

export const NOTIFICATION_TYPES = {
  RATING_PROMPT: 'RATING_PROMPT',
  RATING_RECEIVED: 'RATING_RECEIVED',
  POINTS_EARNED: 'POINTS_EARNED',
  OFFER_RECEIVED: 'offer',
  OFFER_ACCEPTED: 'offer:accepted',
  OFFER_DECLINED: 'offer:declined',
  OFFER_UPDATE: 'OFFER_UPDATE',
  SYSTEM: 'system',
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  NEW_MESSAGE: 'NEW_MESSAGE',
};

// Icon mapping for each notification type
export const NOTIFICATION_ICONS = {
  [NOTIFICATION_TYPES.RATING_PROMPT]: undefined,
  [NOTIFICATION_TYPES.RATING_RECEIVED]: undefined,
  [NOTIFICATION_TYPES.POINTS_EARNED]: undefined,
  [NOTIFICATION_TYPES.OFFER_RECEIVED]: undefined,
  [NOTIFICATION_TYPES.OFFER_ACCEPTED]: undefined,
  [NOTIFICATION_TYPES.OFFER_DECLINED]: undefined,
  [NOTIFICATION_TYPES.OFFER_UPDATE]: undefined,
  [NOTIFICATION_TYPES.SYSTEM]: undefined,
  [NOTIFICATION_TYPES.INFO]: undefined,
  [NOTIFICATION_TYPES.SUCCESS]: undefined,
  [NOTIFICATION_TYPES.WARNING]: undefined,
  [NOTIFICATION_TYPES.ERROR]: undefined,
  [NOTIFICATION_TYPES.NEW_MESSAGE]: undefined,
  default: undefined,
};

// Minimal color scheme - standard PWA colors only
// SUCCESS/WARNING/ERROR keep distinct colors for meaning; others use neutral
export const NOTIFICATION_COLORS = {
  [NOTIFICATION_TYPES.SUCCESS]: {
    bg: 'bg-farmgreen-50 dark:bg-farmgreen-950/10',
    border: 'border-farmgreen-200 dark:border-farmgreen-900/20',
    text: 'text-farmgreen-700 dark:text-farmgreen-300',
  },
  [NOTIFICATION_TYPES.WARNING]: {
    bg: 'bg-farmgold-50 dark:bg-farmgold-950/10',
    border: 'border-farmgold-200 dark:border-farmgold-900/20',
    text: 'text-farmgold-700 dark:text-farmgold-300',
  },
  [NOTIFICATION_TYPES.ERROR]: {
    bg: 'bg-red-50 dark:bg-red-950/10',
    border: 'border-red-200 dark:border-red-900/20',
    text: 'text-red-700 dark:text-red-300',
  },
  // Default neutral for everything else (system, info, offers, etc.)
  default: {
    bg: 'bg-gray-50 dark:bg-gray-950/10',
    border: 'border-gray-200 dark:border-gray-900/20',
    text: 'text-gray-700 dark:text-gray-300',
  },
};

// Resolve colors by type: success/warning/error distinct, everything else neutral
export const resolveNotificationColors = (type) => {
  if (!type) return NOTIFICATION_COLORS.default;
  const normalized = String(type).toLowerCase();
  if (normalized === 'success' || normalized.includes('accepted')) return NOTIFICATION_COLORS[NOTIFICATION_TYPES.SUCCESS];
  if (normalized === 'warning') return NOTIFICATION_COLORS[NOTIFICATION_TYPES.WARNING];
  if (normalized === 'error' || normalized.includes('declined') || normalized.includes('rejected')) return NOTIFICATION_COLORS[NOTIFICATION_TYPES.ERROR];
  return NOTIFICATION_COLORS.default;
};

// Toast configuration - simple, no progress bar complexity
export const TOAST_CONFIG = {
  duration: 4000,
  position: 'bottom-right',
  maxVisible: 3,
};

// Simple toast styling
export const TOAST_STYLES = {
  container: 'fixed z-50 flex flex-col gap-2 pointer-events-none',
  positions: {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4 sm:bottom-20',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 sm:bottom-20',
    'bottom-right': 'bottom-4 right-4 sm:bottom-20',
  },
  toast: {
    base: 'w-full max-w-sm rounded-lg border shadow-lg transition-colors',
    enter: 'animate-in slide-in-from-top fade-in',
    exit: 'animate-out slide-out-to-top fade-out',
    content: 'flex items-start gap-3 p-4',
    iconWrapper: 'flex-shrink-0',
    textWrapper: 'flex-1 min-w-0',
    title: 'text-sm font-semibold',
    message: 'text-sm mt-1 opacity-90',
    closeBtn: 'flex-shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors',
    progress: 'h-1 w-full absolute bottom-0 left-0 origin-left',
  },
};

export const getNotificationConfig = (type) => {
  const colors = resolveNotificationColors(type);
  return {
    colors,
  };
};

export const normalizeNotificationType = (type) => {
  if (!type) return 'system';
  const typeStr = String(type).toLowerCase();
  if (typeStr === 'offer' || typeStr === 'offer_received') return NOTIFICATION_TYPES.OFFER_RECEIVED;
  if (typeStr === 'offer:accepted' || typeStr === 'offer_accepted') return NOTIFICATION_TYPES.OFFER_ACCEPTED;
  if (typeStr === 'offer:declined' || typeStr === 'offer_rejected' || typeStr === 'offer_declined') return NOTIFICATION_TYPES.OFFER_DECLINED;
  const upperType = type.toUpperCase();
  if (NOTIFICATION_TYPES[upperType]) return NOTIFICATION_TYPES[upperType];
  if (NOTIFICATION_TYPES[type]) return type;
  return 'system';
};

export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const date = timestamp.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffWeek < 4) return `${diffWeek}w ago`;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

export default {
  TYPES: NOTIFICATION_TYPES,
  ICONS: NOTIFICATION_ICONS,
  COLORS: NOTIFICATION_COLORS,
  resolveColors: resolveNotificationColors,
  TOAST: TOAST_CONFIG,
  TOAST_STYLES,
  getConfig: getNotificationConfig,
  normalizeType: normalizeNotificationType,
  formatTime: formatRelativeTime,
};
